import { useState, useCallback } from "react";

const BRANDING_STORAGE_KEY = "eScribe_signing_branding";

/**
 * Default branding values
 */
const defaultBranding = {
  companyName: "eScribe",
  logoUrl: null,
  logoDarkUrl: null,
  primaryColor: "#6366f1",
  hidePoweredBy: false,
  signingPageTitle: null,
  signingPageWelcomeMessage: null,
  signingPageCompletionMessage: null,
  signingPageBackgroundUrl: null,
  customTermsUrl: null,
  customPrivacyUrl: null,
};

/**
 * Hook to manage signing branding across signing pages
 * Uses sessionStorage to persist branding during the signing session
 */
export const useSigningBranding = () => {
  const [branding, setBranding] = useState(() => {
    // Initialize from sessionStorage
    try {
      const stored = sessionStorage.getItem(BRANDING_STORAGE_KEY);
      if (stored) {
        return { ...defaultBranding, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.error("Failed to parse stored branding:", e);
    }
    return defaultBranding;
  });

  // Set branding and persist to sessionStorage
  const updateBranding = useCallback((newBranding) => {
    if (newBranding) {
      const merged = { ...defaultBranding, ...newBranding };
      setBranding(merged);
      try {
        sessionStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(merged));
      } catch (e) {
        console.error("Failed to store branding:", e);
      }
    }
  }, []);

  // Clear branding from sessionStorage
  const clearBranding = useCallback(() => {
    setBranding(defaultBranding);
    try {
      sessionStorage.removeItem(BRANDING_STORAGE_KEY);
    } catch (e) {
      console.error("Failed to clear branding:", e);
    }
  }, []);

  // Computed values
  const isWhiteLabeled =
    branding.companyName && branding.companyName !== "eScribe";
  const displayName = branding.companyName || "eScribe";
  const showPoweredBy = !branding.hidePoweredBy;

  const getLogoUrl = useCallback(
    (isDark = false) => {
      if (isDark && branding.logoDarkUrl) return branding.logoDarkUrl;
      return branding.logoUrl || null;
    },
    [branding.logoUrl, branding.logoDarkUrl],
  );

  return {
    branding,
    setBranding: updateBranding,
    clearBranding,
    isWhiteLabeled,
    displayName,
    showPoweredBy,
    getLogoUrl,
    primaryColor: branding.primaryColor || "#6366f1",
  };
};

export default useSigningBranding;
